import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../utils/permissions";
import {
  LoaderPinwheel,
  Warehouse,
  Star,
  Package,
  Pencil,
  Trash2,
  X,
} from "lucide-react";

const WarehousesPage = () => {
  const [almacenes, setAlmacenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAlmacen, setSelectedAlmacen] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    tipo: "secundario",
  });

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8001/api";
  const navigate = useNavigate();

  const fetchAlmacenes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/almacenes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlmacenes(res.data);
    } catch {
      toast.error("No se pudieron cargar los almacenes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlmacenes();
    // eslint-disable-next-line
  }, []);

  const handleCreate = () => {
    setSelectedAlmacen(null);
    setFormData({ nombre: "", descripcion: "", tipo: "secundario" });
    setShowModal(true);
  };

  const handleEdit = (almacen) => {
    setSelectedAlmacen(almacen);
    setFormData({
      nombre: almacen.nombre,
      descripcion: almacen.descripcion || "",
      tipo: almacen.tipo,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    try {
      if (selectedAlmacen) {
        await axios.put(
          `${API_URL}/almacenes/${selectedAlmacen._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Almacén actualizado correctamente");
      } else {
        await axios.post(`${API_URL}/almacenes`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Almacén creado correctamente");
      }
      setShowModal(false);
      fetchAlmacenes();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar el almacén");
    }
  };

  const handleDelete = async (id, esPrincipal) => {
    if (esPrincipal) {
      toast.error("No se puede eliminar el almacén principal");
      return;
    }
    if (!window.confirm("¿Desea desactivar este almacén?")) return;
    try {
      await axios.delete(`${API_URL}/almacenes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Almacén desactivado correctamente");
      fetchAlmacenes();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error al desactivar el almacén",
      );
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Gestión de Almacenes
          </h2>
          <p className="text-sm text-gray-500">
            {almacenes.length} almacén{almacenes.length !== 1 ? "es" : ""}{" "}
            registrado{almacenes.length !== 1 ? "s" : ""}
          </p>
        </div>
        {hasPermission("almacen.crear") && (
          <Button
            onClick={handleCreate}
            data-testid="create-warehouse-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            + Nuevo almacén
          </Button>
        )}
      </div>

      {loading && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
          <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
          Cargando datos, por favor espera...
        </div>
      )}

      {/* Grid de almacenes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {almacenes.map((almacen) => (
          <div
            key={almacen._id}
            data-testid={`warehouse-card-${almacen._id}`}
            className="relative bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
          >
            {/* Badge tipo */}
            <div className="absolute top-4 right-4">
              {almacen.esPrincipal ? (
                <span className="flex items-center gap-1 px-2.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-semibold">
                  <Star className="h-3 w-3" /> Principal
                </span>
              ) : (
                <span className="px-2.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 rounded-full text-xs font-semibold">
                  Secundario
                </span>
              )}
            </div>

            {/* Icono + nombre */}
            <div className="flex items-start gap-3 mb-3 pr-24">
              <div
                className={`p-2 rounded-lg ${almacen.esPrincipal ? "bg-amber-50" : "bg-blue-50"}`}
              >
                <Warehouse
                  className={`h-5 w-5 ${almacen.esPrincipal ? "text-amber-600" : "text-blue-600"}`}
                />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-800 truncate">
                  {almacen.nombre}
                </h3>
                {almacen.descripcion && (
                  <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                    {almacen.descripcion}
                  </p>
                )}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex gap-2 pt-3 border-t border-gray-100 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/almacen/${almacen._id}/stock`)}
                data-testid={`view-stock-btn-${almacen._id}`}
                className="flex-1 text-xs"
              >
                <Package className="h-3.5 w-3.5 mr-1" /> Ver stock
              </Button>
              {hasPermission("almacen.editar") && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(almacen)}
                  data-testid={`edit-warehouse-btn-${almacen._id}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {!almacen.esPrincipal && hasPermission("almacen.eliminar") && (
                <Button
                  size="sm"
                  className="bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => handleDelete(almacen._id, almacen.esPrincipal)}
                  data-testid={`delete-warehouse-btn-${almacen._id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {almacenes.length === 0 && !loading && (
          <div className="col-span-full py-16 text-center">
            <Warehouse className="h-12 w-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              No hay almacenes registrados
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Cree el almacén principal para comenzar
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="warehouse-modal"
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold text-gray-800">
                {selectedAlmacen ? "Editar almacén" : "Nuevo almacén"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                  data-testid="warehouse-name-input"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Ej: Almacén Central"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) =>
                    setFormData({ ...formData, descripcion: e.target.value })
                  }
                  data-testid="warehouse-description-input"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Descripción opcional"
                  rows="3"
                />
              </div>

              {!selectedAlmacen && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <select
                    value={formData.tipo}
                    onChange={(e) =>
                      setFormData({ ...formData, tipo: e.target.value })
                    }
                    data-testid="warehouse-type-select"
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="principal">Principal</option>
                    <option value="secundario">Secundario</option>
                  </select>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  data-testid="cancel-warehouse-btn"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="save-warehouse-btn"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {selectedAlmacen ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehousesPage;
